import { AxiosRequestConfig, AxiosResponse } from "axios";
import kebabcase from "lodash.kebabcase";
import snakeCase from "lodash.snakecase";

import { envChecker } from "./envChecker";
import { isObjectEmpty } from "./isObjectEmpty";

import { transformer } from "helpers/transformer";
import { axios } from "lib/axios";

import { EENV } from "constants/index";

export type TFetcherConfig<T, A = unknown> = AxiosRequestConfig<T> & {
  isPublic?: boolean;
  ignoredKeysReq?: string[];
  ignoredKeysRes?: string[];
  urlData?: A;
  fileFields?: (keyof T)[];
  namingTransformer?: (string?: string) => string;
};

export type TFetcherError = {
  message: string;
  code: string;
  data: any;
  payload: {
    params: any;
    data: any;
    baseUrl?: string;
    path?: string;
  };
};

export const fetcher = <TRes, TReq, TParam = unknown>(
  { url, method }: { url: string; method: string },
  {
    method: configMethod,
    url: configUrl,
    fileFields,
    data,
    params,
    headers,
    urlData,
    signal,
    namingTransformer,
    ...config
  }: TFetcherConfig<TReq, TParam> = {}
): Promise<AxiosResponse<TRes>> => {
  const transformerFn = namingTransformer || snakeCase;

  let transformedData = transformer(data, transformerFn, [
    ...(config.ignoredKeysReq || []),
    ...((fileFields as string[]) || []),
  ]);

  const transforedParams = transformer(params, kebabcase);

  if (!isObjectEmpty(urlData)) {
    const _urlData = urlData as any;
    // Replace each placeholder in the URL with the corresponding value from urlData
    url = url.replace(/{(.*?)}/g, (match, key) => {
      return key in _urlData! ? _urlData[key] : match;
    });
  }

  if (fileFields?.length) {
    transformedData = Object.keys(transformedData)
      .filter((key) => !fileFields.includes(key as keyof TReq))
      .reduce(
        (acc, key) => {
          acc[key] = transformedData[key];
          return acc;
        },
        {} as Record<string, any>
      );

    const formData = new FormData();

    for (const [key, value] of Object.entries(transformedData)) {
      formData.append(key, value as string | Blob);
    }

    if (data !== null && typeof data === "object") {
      for (const fileField of fileFields) {
        const transformedKey = transformerFn(fileField as string);
        const file = (data as Record<string, unknown>)[fileField as string];
        if (file instanceof File || file instanceof Blob) {
          formData.append(transformedKey, file);
        } else {
          if (envChecker({ envs: [EENV.DEV] })) {
            console.warn(
              `The value for field "${String(fileField)}" is not a valid File or Blob.`
            );
          }
        }
      }
    }
    transformedData = formData;
    headers = { ...headers, "Content-Type": "multipart/form-data" };
  }

  return axios({
    url: url || configUrl,
    method: method || configMethod,
    data: transformedData,
    params: transforedParams,
    headers,
    signal,
    ...config,
  });
};
